<template>
    <section class="document-scraping-panel">
        <div class="document-scraping-content">
            <div class="document-scraping-heading">
                <q-icon name="upload_file" color="primary" size="24px" />
                <div>
                    <h3>Upload referral document</h3>
                    <p>Select the referral type and its PDF or DOCX file.</p>
                </div>
            </div>
            <div class="row q-col-gutter-md">
                <div class="col-12 col-md-5">
                    <q-select
                        :model-value="formType"
                        :options="formTypeOptions"
                        label="Form type"
                        outlined
                        dense
                        emit-value
                        map-options
                        :loading="loadingDefinitions"
                        :disable="loading || loadingDefinitions"
                        @update:model-value="setFormType"
                    />
                </div>
                <div class="col-12 col-md-7">
                    <q-file
                        :model-value="file"
                        label="PDF or DOCX referral"
                        outlined
                        dense
                        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        :disable="loading"
                        @update:model-value="setFile"
                    >
                        <template #prepend><q-icon name="attach_file" /></template>
                    </q-file>
                </div>
            </div>

            <div class="row justify-end q-mt-md">
                <q-btn
                    unelevated
                    no-caps
                    color="primary"
                    label="Extract referral"
                    :loading="loading"
                    :disable="!formType || !file"
                    @click="extractReferral"
                />
            </div>

            <section v-if="result" class="document-review q-mt-lg">
                <h3>Review extracted information</h3>
                <p class="text-grey-7">
                    Check the values below and correct anything that was not read properly.
                </p>

                <div
                    v-for="group in reviewGroups"
                    :key="group.key"
                    class="document-review-group"
                >
                    <h4>{{ group.label }}</h4>
                    <div class="document-review-grid">
                        <q-input
                            v-for="field in group.fields"
                            :key="field.originalFieldKey"
                            :model-value="editableValue(field.reviewedValue)"
                            :label="field.label"
                            placeholder="Not provided"
                            outlined
                            dense
                            autogrow
                            type="textarea"
                            hide-bottom-space
                            @update:model-value="value => updateReviewedValue(field.originalFieldKey, value)"
                        />
                    </div>
                </div>

                <div class="row justify-end q-gutter-sm q-mt-lg">
                    <q-btn flat no-caps color="negative" label="Start over" @click="declineResult" />
                    <q-btn
                        unelevated
                        no-caps
                        color="primary"
                        label="Use this information"
                        @click="acceptResult"
                    />
                </div>
            </section>
        </div>
    </section>
</template>

<script>
import api from "@/services/api/api";
import notify from "@/services/util/notify";
import {
    displayReferralValue,
    updateReviewedField,
} from "@/services/util/referralWorkflow";

export default {
    name: "DocumentScrapingPanel",
    emits: ["accepted", "changed", "declined", "status"],
    data() {
        return {
            formType: null,
            definitions: [],
            file: null,
            loading: false,
            loadingDefinitions: false,
            result: null,
            reviewedFields: [],
        };
    },
    computed: {
        formTypeOptions() {
            return this.definitions.map(definition => ({
                label: definition.name,
                value: definition.formType,
            }));
        },
        reviewGroups() {
            const groups = [
                {
                    key: "person",
                    label: "Person details",
                    fields: this.reviewedFields.filter(field => field.destination?.entity === "person"),
                },
                {
                    key: "engagement",
                    label: "Client Engagement",
                    fields: this.reviewedFields.filter(field => field.destination?.entity === "engagement"),
                },
                {
                    key: "referral",
                    label: "Referral-specific information",
                    fields: this.reviewedFields.filter(field => !field.destination),
                },
            ];
            return groups.filter(group => group.fields.length);
        },
    },
    mounted() {
        this.loadDefinitions();
    },
    methods: {
        emitStatus() {
            this.$emit("status", {
                hasFile: Boolean(this.file),
                hasResult: Boolean(this.result),
                loading: this.loading,
            });
        },
        setFormType(formType) {
            this.formType = formType;
            this.$emit("changed");
        },
        setFile(file) {
            this.file = file;
            this.$emit("changed");
            this.emitStatus();
        },
        editableValue(value) {
            return value == null || value === "" ? "" : displayReferralValue(value);
        },
        async loadDefinitions() {
            this.loadingDefinitions = true;
            try {
                const response = await api.documentScraping.listActiveFormDefinitions();
                this.definitions = response.data || [];
                this.formType = this.formType || this.definitions[0]?.formType || null;
            } catch (error) {
                const message = error?.response?.statusText;
                notify.error(
                    message && message !== "Internal Server Error"
                        ? message
                        : "Failed to load referral form types",
                    "top"
                );
            } finally {
                this.loadingDefinitions = false;
            }
        },
        async extractReferral() {
            if (!this.formType || !this.file) return;

            this.loading = true;
            this.result = null;
            this.reviewedFields = [];
            this.emitStatus();
            try {
                const response = await api.documentScraping.processDocument(this.formType, this.file);
                this.result = response.data;
                this.reviewedFields = response.data.mappedFields.map(field => ({
                    ...field,
                    destination: field.destination ? { ...field.destination } : null,
                    metadata: field.metadata ? { ...field.metadata } : null,
                }));
            } catch (error) {
                const responseMessage = typeof error?.response?.data === "string" ? error.response.data : null;
                notify.error(responseMessage || "Failed to extract referral information", "top");
            } finally {
                this.loading = false;
                this.emitStatus();
            }
        },
        updateReviewedValue(fieldKey, value) {
            this.reviewedFields = updateReviewedField(this.reviewedFields, fieldKey, value);
            this.$emit("changed");
        },
        acceptResult() {
            this.$emit("accepted", {
                definitionId: this.result.definitionId,
                definitionUpdatedAt: this.result.definitionUpdatedAt,
                formType: this.result.formType,
                formName: this.result.formName,
                fields: this.reviewedFields.map(field => ({
                    ...field,
                    destination: field.destination ? { ...field.destination } : null,
                    metadata: field.metadata ? { ...field.metadata } : null,
                })),
                file: this.file,
            });
            this.result = null;
            this.reviewedFields = [];
            this.file = null;
            this.emitStatus();
        },
        declineResult() {
            this.result = null;
            this.reviewedFields = [];
            this.file = null;
            this.$emit("declined");
            this.emitStatus();
        },
    },
};
</script>

<style scoped>
.document-scraping-panel {
    margin-bottom: 24px;
    border: 1px solid rgba(47, 62, 70, 0.14);
    border-radius: 6px;
}

.document-scraping-content {
    padding: 18px 16px 20px;
}

.document-scraping-heading {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    margin-bottom: 18px;
}

.document-scraping-heading h3,
.document-review h3,
.document-review-group h4 {
    margin: 0;
    color: #25434a;
    font-weight: 650;
}

.document-scraping-heading h3,
.document-review h3 {
    font-size: 1.05rem;
}

.document-scraping-heading p {
    margin: 2px 0 0;
    color: #5d6b70;
}

.document-review-group {
    margin-top: 18px;
}

.document-review-group h4 {
    margin-bottom: 10px;
    font-size: 0.95rem;
}

.document-review-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
}

@media (max-width: 700px) {
    .document-review-grid {
        grid-template-columns: 1fr;
    }
}
</style>
